import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import * as pseudoProjectApi from '../../services/pseudo-project-api.js';

@customElement('cp-pseudo-project-add')
export class CpPseudoProjectAdd extends LitElement {
  createRenderRoot() { return this; }

  @state() private _open = false;
  @state() private _nameValue = '';
  @state() private _submitting = false;
  @state() private _submitError = '';
  @state() private _nameError = '';

  open(): void {
    this._resetForm();
    this._open = true;
    this.updateComplete.then(() => {
      const input = this.querySelector<HTMLInputElement>('.cp-pseudo-project-add__input');
      input?.focus();
    });
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  private _close(): void {
    this._open = false;
  }

  private _resetForm(): void {
    this._nameValue = '';
    this._submitting = false;
    this._submitError = '';
    this._nameError = '';
  }

  private _onNameInput(e: Event): void {
    this._nameValue = (e.target as HTMLInputElement).value;
    this._nameError = '';
    this._submitError = '';
  }

  private _onBackdropClick(e: Event): void {
    if ((e.target as HTMLElement).classList.contains('cp-pseudo-project-add__overlay')) {
      this._close();
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this._open) {
      this._close();
    }
  };

  private async _onSubmit(): Promise<void> {
    const name = this._nameValue.trim();

    if (!name) {
      this._nameError = 'Name ist erforderlich';
      return;
    }

    if (this._submitting) return;

    this._submitting = true;
    this._submitError = '';

    try {
      const project = await pseudoProjectApi.createPseudoProject({ name });
      this._close();
      this.dispatchEvent(new CustomEvent('pseudo-project-created', {
        bubbles: true,
        composed: true,
        detail: { project },
      }));
    } catch (e) {
      this._submitError = e instanceof Error ? e.message : 'Pseudo-Projekt konnte nicht erstellt werden';
    } finally {
      this._submitting = false;
    }
  }

  private _onFormKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !this._submitting) {
      e.preventDefault();
      void this._onSubmit();
    }
  }

  render() {
    if (!this._open) return nothing;

    const canSubmit = this._nameValue.trim().length > 0 && !this._submitting;

    return html`
      <div class="cp-pseudo-project-add__overlay" @click=${this._onBackdropClick}>
        <div class="cp-pseudo-project-add__dialog" @keydown=${this._onFormKeyDown}>
          <div class="cp-pseudo-project-add__header">
            <h2 class="cp-pseudo-project-add__title">Neues Pseudo-Projekt</h2>
            <button class="cp-pseudo-project-add__close" @click=${this._close}>&times;</button>
          </div>

          <div class="cp-pseudo-project-add__body">
            <div class="cp-pseudo-project-add__field">
              <label class="cp-pseudo-project-add__label">Name</label>
              <input
                class="cp-pseudo-project-add__input ${this._nameError ? 'cp-pseudo-project-add__input--error' : ''}"
                type="text"
                placeholder="z.B. Haushalt, Fitness, Lernen..."
                .value=${this._nameValue}
                @input=${this._onNameInput}
              />
              ${this._nameError ? html`
                <p class="cp-pseudo-project-add__validation-msg">${this._nameError}</p>
              ` : nothing}
            </div>

            ${this._submitError ? html`
              <div class="cp-pseudo-project-add__error">${this._submitError}</div>
            ` : nothing}
          </div>

          <div class="cp-pseudo-project-add__footer">
            <button class="cp-pseudo-project-add__btn cp-pseudo-project-add__btn--cancel" @click=${this._close}>Abbrechen</button>
            <button
              class="cp-pseudo-project-add__btn cp-pseudo-project-add__btn--submit"
              ?disabled=${!canSubmit}
              @click=${this._onSubmit}
            >${this._submitting ? 'Wird erstellt...' : 'Erstellen'}</button>
          </div>
        </div>
      </div>
    `;
  }
}

// Auto-attach modal to DOM and listen for trigger events
let modalInstance: CpPseudoProjectAdd | null = null;

function getModal(): CpPseudoProjectAdd {
  if (!modalInstance || !modalInstance.isConnected) {
    modalInstance = document.createElement('cp-pseudo-project-add') as CpPseudoProjectAdd;
    document.body.appendChild(modalInstance);
  }
  return modalInstance;
}

document.addEventListener('pseudo-project-add-request', () => {
  getModal().open();
});

declare global {
  interface HTMLElementTagNameMap {
    'cp-pseudo-project-add': CpPseudoProjectAdd;
  }
}
